/**
 * Pipeline / line feature presentation on map popups (aligned with legacy `mapFeatureProps.js`).
 */

export const MAPBOX_PIPELINE_BUXUS_ID_KEY = "BUXUS_ID";

export const STRING_TYPE = 0;
export const INT_DIAM_INCHES_TYPE = 1;
export const FLOAT_LENGTH_FEET_TYPE = 2;
export const PHONE_TYPE = 3;

export type MapFeaturePropRow = {
  key: string;
  label: string;
  type: number;
  valueSuffix?: string;
};

/** Property keys to test for “pipeline-like” vector features under the cursor (legacy behavior). */
export const mapFeatureProps: readonly MapFeaturePropRow[] = [
  { key: "CO_NAME", label: "Operator", type: STRING_TYPE },
  { key: "PRODUCT", label: "Product", type: STRING_TYPE },
  { key: "PIPE_TYPE", label: "Line Type", type: STRING_TYPE },
  { key: "SYS_NAME", label: "Line Name", type: STRING_TYPE },
  { key: "EM_PHONE", label: "Emergency", type: PHONE_TYPE },
  // { key: "Shape_Leng", label: "Length", type: FLOAT_LENGTH_FEET_TYPE, valueSuffix: " ft" },
  { key: "MATERIAL", label: "Material", type: STRING_TYPE },
  { key: "PRESSURE", label: "System Pressure", type: STRING_TYPE },
  { key: "OD", label: "Nominal Diam", type: INT_DIAM_INCHES_TYPE, valueSuffix: " in" },
  { key: "LEGAL_DESC", label: "Legal Desc", type: STRING_TYPE },
  { key: "OWNER", label: "Owner", type: STRING_TYPE },
] as const;

const POPUP_LEAD_KEYS = ["CO_NAME", "PRODUCT"] as const;

function isEmergencyMapFeatureProp(prop: MapFeaturePropRow): boolean {
  if (prop.key === "EM_PHONE") return true;
  if (/^EM_/i.test(prop.key)) return true;
  return prop.label.toLowerCase().includes("emergency");
}

/** Pipeline popup field order: ID (caller), Operator, Product, emergency phone(s), then catalog order. */
export function orderMapFeaturePropsForPopup(
  props: readonly MapFeaturePropRow[] = mapFeatureProps,
): MapFeaturePropRow[] {
  const byKey = new Map(props.map((row) => [row.key, row]));
  const ordered: MapFeaturePropRow[] = [];
  const placed = new Set<string>();

  const place = (key: string) => {
    const row = byKey.get(key);
    if (!row || placed.has(key)) return;
    ordered.push(row);
    placed.add(key);
  };

  for (const key of POPUP_LEAD_KEYS) place(key);

  const emergency = props
    .filter((row) => !placed.has(row.key) && isEmergencyMapFeatureProp(row))
    .sort((a, b) => {
      if (a.key === "EM_PHONE") return -1;
      if (b.key === "EM_PHONE") return 1;
      return props.indexOf(a) - props.indexOf(b);
    });
  for (const row of emergency) {
    ordered.push(row);
    placed.add(row.key);
  }

  for (const row of props) {
    if (!placed.has(row.key)) ordered.push(row);
  }

  return ordered;
}

const PIPELINE_PROP_KEYS = new Set(mapFeatureProps.map((p) => p.key));

/** True when a pipeline attribute should be shown in map popups (non-empty after trim). */
export function isMapFeaturePropPresent(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (typeof v === "number") return v !== 0 && Number.isFinite(v);
  return true;
}

/** True when the feature carries at least one known pipeline attribute (mirrors legacy mobile `hasProps`). */
export function featureHasPipelineProps(feature: {
  properties?: Record<string, unknown> | null;
}): boolean {
  const p = feature.properties;
  if (!p) return false;
  for (const key of PIPELINE_PROP_KEYS) {
    if (Object.prototype.hasOwnProperty.call(p, key) && isMapFeaturePropPresent(p[key])) return true;
  }
  return false;
}
